import { AlertTriangle, RotateCcw } from "lucide-react";

export const STATUS_TONES = {
  open: "pill-gray",
  draft: "pill-gray",
  submitted: "pill-gray",
  "pending approval": "pill-gray",
  approved: "pill-gray",
  "client review": "pill-gray",
  closed: "pill-gray",
  completed: "pill-gray",
  "in progress": "pill-amber",
  recruiting: "pill-amber",
  filled: "pill-green",
  "on hold": "pill-red",
  assigned: "pill-teal",
  confirmed: "pill-teal",
};

export const statusKey = (status) => (status || "").toLowerCase();

export const StatusPill = ({ status, testId }) => (
  <span className={`board-pill ${STATUS_TONES[statusKey(status)] || "pill-gray"}`} data-testid={testId}>
    {status}
  </span>
);

export const PriorityCell = ({ priority, testId }) =>
  priority === "Standard" ? (
    <span className="priority-plain" data-testid={testId}>Standard</span>
  ) : (
    <span className={`board-pill ${priority === "Urgent" ? "pill-red" : "pill-amber"}`} data-testid={testId}>
      {priority}
    </span>
  );

export const Dash = ({ testId }) => (
  <span className="cell-dash" data-testid={testId} aria-label="Not set">–</span>
);

export const UnassignedCell = ({ label, testId, value }) =>
  value ? <span data-testid={testId}>{value}</span> : (
    <span className="cell-unassigned" data-testid={testId}>
      <AlertTriangle size={13} /> {label}
    </span>
  );

export function formatDate(value, withTime = false) {
  if (!value) {
    return "";
  }
  // Date-only values (YYYY-MM-DD) are calendar days, not instants: parse them as local time so they never shift a day.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return withTime
    ? date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : date.toLocaleDateString(undefined, { dateStyle: "medium" });
}

export const isInProgress = (row) => ["in progress", "recruiting"].includes(statusKey(row.status));

export const ageLabel = (row) => (row.age_days === null || row.age_days === undefined ? "" : `${row.age_days}d`);

export const refillLabel = (row) => {
  if (row.time_to_refill_days === null || row.time_to_refill_days === undefined) return "";
  return row.refill_in_progress ? `${row.time_to_refill_days}d so far` : `refilled in ${row.time_to_refill_days}d`;
};

export const AgeCell = ({ row, testId }) =>
  row.age_days === null || row.age_days === undefined ? <Dash testId={testId} /> : (
    <span className={`board-age ${row.age_since === "reopened" ? "board-age-reopened" : ""}`} data-testid={testId} title={row.age_since === "reopened" ? `Counted from the latest reopening (${formatDate(row.reopened_at)}); original request ${formatDate(row.original_request_date)}` : `Counted from the original request date (${formatDate(row.original_request_date)})`}>
      <strong>{ageLabel(row)}</strong>
      {row.age_since === "reopened" && <small><RotateCcw size={10} /> reopened{row.reopen_count > 1 ? ` ×${row.reopen_count}` : ""}{refillLabel(row) ? ` · ${refillLabel(row)}` : ""}</small>}
    </span>
  );

export function exportRowsCsv(rows) {
  const header = ["Request ID", "Client", "Role", "Priority", "Status", "Recruiter", "Candidate", "Date needed", "Original request date", "Age (days)", "Age counted from", "Reopened", "Time to refill (days)", "Last updated"];
  const escape = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const body = rows.map((row) =>
    [row.id, row.client_name, row.role_name, row.priority, row.status, row.assigned_recruiter, row.assigned_candidate, row.needed_date, (row.original_request_date || "").slice(0, 10), row.age_days, row.age_since === "reopened" ? `reopening ${(row.reopened_at || "").slice(0, 10)}` : "request date", row.reopen_count || 0, row.time_to_refill_days ?? "", row.last_updated]
      .map(escape)
      .join(","),
  );
  const blob = new Blob([[header.map(escape).join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `staffing-board-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
